/**
 * Componente Alpine.js para formulario PQRS
 * @author carlos rodriguez
 * @since 2025
 */

export function pqrsPage(tramitesUrl, tramitePlaceholder, tiposUrl) {
    return {
        isLoading: false,
        pqrCreada: false,
        pqrData: { id: null, tipo: '', fecha: '', estado: '' },
        errores: {},
        touched: {},
        tramites: [],
        tipos: [],
        tramitePlaceholder,
        tramitesUrl,
        tiposUrl,
        _esAnonima: false,
        choicesTramite: null,
        choicesTipo: null,
        mostrarPDF: false,
        urlPDF: '',
        tituloPDF: '',
        scrollPos: 0,

        get esAnonima() {
            return this._esAnonima;
        },
        set esAnonima(val) {
            this._esAnonima = val;
        },

        abrirPDF(url, titulo) {
            // Guardar posición del scroll
            this.scrollPos = window.pageYOffset || document.documentElement.scrollTop;
            this.urlPDF = url;
            this.tituloPDF = titulo;
            this.mostrarPDF = true;
        },

        cerrarPDF() {
            this.mostrarPDF = false;
            this.urlPDF = '';
            this.tituloPDF = '';
            // Restaurar posición del scroll después de un pequeño delay
            setTimeout(() => {
                window.scrollTo(0, this.scrollPos);
            }, 100);
        },

        campos() {
            return {
                tipo: this.$refs.tipo?.value || "",
                tramite: this.$refs.tramite?.value || "",
                placa: this.$refs.placa?.value || "",
                name: !this.esAnonima ? (this.$refs.name?.value || "") : "ok",
                apellidos: !this.esAnonima ? (this.$refs.apellidos?.value || "") : "ok",
                document: !this.esAnonima ? (this.$refs.document?.value || "") : "ok",
                email: this.$refs.email?.value || "",
                phone: this.$refs.phone?.value || "",
                message: this.$refs.message?.value || "",
                terms: this.$refs.terms?.checked,
                data_treatment: this.$refs.data_treatment?.checked,
            };
        },

        getReglas() {
            return [
                { campo: "tipo", cond: c => !c.tipo, msg: "Campo requerido" },
                { campo: "tramite", cond: c => !c.tramite, msg: "Campo requerido" },
                { campo: "placa", cond: c => !c.placa, msg: "Campo requerido" },
                { campo: "placa", cond: c => c.placa && c.placa.length !== 6, msg: "La placa debe tener exactamente 6 caracteres. Ejemplo: ABC123" },
                { campo: "name", cond: c => !this.esAnonima && !c.name, msg: "Campo requerido" },
                { campo: "apellidos", cond: c => !this.esAnonima && !c.apellidos, msg: "Campo requerido" },
                { campo: "document", cond: c => !this.esAnonima && !c.document, msg: "Campo requerido" },
                { campo: "email", cond: c => !c.email, msg: "Campo requerido" },
                { campo: "email", cond: c => c.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email), msg: "Formato de correo no válido. Usa uno como: nombre@ejemplo.com" },
                { campo: "message", cond: c => !c.message || c.message.length < 10, msg: "El mensaje debe tener al menos 10 caracteres" },
                { campo: "terms", cond: c => !c.terms, msg: "Debe aceptar los términos" },
                { campo: "data_treatment", cond: c => !c.data_treatment, msg: "Debe aceptar el tratamiento de datos" },
                { campo: "phone", cond: c => !c.phone, msg: "Campo requerido" },
                { campo: "phone", cond: c => c.phone && c.phone.length !== 10, msg: "El teléfono debe tener exactamente 10 dígitos. Ejemplo: 3123456789" },
                { campo: "phone", cond: c => c.phone && !/^[36]\d{9}$/.test(c.phone), msg: "El teléfono debe contener solo números y comenzar por 3 o 6. Ejemplo: 3123456789" },
            ];
        },

        marcarTocado(campo) {
            this.touched[campo] = true;
            this.validar();
        },

        validar() {
            const c = this.campos();
            const reglas = this.getReglas();
            const nuevosErrores = {};
            
            for (const r of reglas) {
                if (this.touched[r.campo] && r.cond(c)) {
                    if (!nuevosErrores[r.campo]) {
                        nuevosErrores[r.campo] = [];
                    }
                    nuevosErrores[r.campo].push(r.msg);
                    break; // Solo mostrar el primer error por campo
                }
            }
            
            this.errores = nuevosErrores;
        },

        validarTodo() {
            const c = this.campos();
            const reglas = this.getReglas();
            this.errores = {};
            
            for (const r of reglas) {
                this.touched[r.campo] = true;
                if (r.cond(c)) {
                    if (!this.errores[r.campo]) {
                        this.errores[r.campo] = [];
                    }
                    this.errores[r.campo].push(r.msg);
                }
            }
            
            return Object.keys(this.errores).length === 0;
        },

        limpiar() {
            this.errores = {};
            this.touched = {};
        },

        async init() {
            this.isLoading = true;
            try {
                const tramitesResp = await fetch(this.tramitesUrl);
                const tramitesJson = await tramitesResp.json();
                this.tramites = Array.isArray(tramitesJson) ? tramitesJson : (tramitesJson.data || []);
                
                const tiposResp = await fetch(this.tiposUrl);
                const tiposJson = await tiposResp.json();
                this.tipos = Array.isArray(tiposJson) ? tiposJson : (tiposJson.data || []);
                
                setTimeout(() => this.initChoices(), 0);
            } catch (e) {
                console.error("Error cargando datos:", e);
            } finally {
                this.isLoading = false;
            }
        },

        initChoices() {
            if (this.choicesTramite) this.choicesTramite.destroy();
            if (this.$refs.tramite) {
                this.choicesTramite = new Choices(this.$refs.tramite, {
                    searchEnabled: true,
                    shouldSort: false,
                    itemSelectText: "",
                    placeholderValue: this.tramitePlaceholder,
                    position: "bottom"
                });
            }
            
            if (this.choicesTipo) this.choicesTipo.destroy();
            if (this.$refs.tipo) {
                this.choicesTipo = new Choices(this.$refs.tipo, {
                    searchEnabled: false,
                    shouldSort: false,
                    itemSelectText: "",
                    placeholderValue: "Seleccione el tipo",
                    position: "bottom"
                });
            }
        },

        toUpperOnlyLetters(e) {
            e.target.value = e.target.value.replace(/[^A-Za-zÁÉÍÓÚáéíóúÑñ\s]/g, '').toUpperCase();
        },
        
        toUpperOnlyNumbers(e) {
            e.target.value = e.target.value.replace(/[^0-9]/g, '');
        },
        
        toUpper(e) {
            e.target.value = e.target.value.toUpperCase();
        },

        async resetearFormulario() {
            // Resetear estado Alpine primero (CRÍTICO: antes de cualquier otra acción)
            this.pqrCreada = false;
            this.isLoading = false;
            this.limpiar();
            this._esAnonima = false;
            
            // Esperar un tick para que Alpine actualice el DOM
            await this.$nextTick();
            
            // Limpiar Dropzone
            document.querySelectorAll('.gestor-dropzone-pdf').forEach(el => {
                if (el.dropzone?.removeAllFiles) el.dropzone.removeAllFiles();
            });
            
            // Limpiar inputs de archivos
            ['filename', 'filename_real', 'filepath'].forEach(id => {
                const el = document.getElementById(id);
                if (el) el.value = '';
            });
            
            // Resetear formulario y selects
            const form = document.getElementById('contact_form');
            if (form) form.reset();
            if (this.choicesTramite) this.choicesTramite.setChoiceByValue('');
            if (this.choicesTipo) this.choicesTipo.setChoiceByValue('');
            
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };
}

// Registrar componente globalmente
window.pqrsPage = pqrsPage;
